/**
 * Question API Client
 * -------------------
 * Handles communication with the Lambda API for fetching questions.
 * Replaces local mock data with server-side MongoDB queries.
 */

import { firebaseAuth } from './firebase.js';
import { decryptResponse } from './crypto.js';

const API_BASE = import.meta.env.VITE_API_BASE;

/**
 * Helper for fetch with error handling
 */
async function apiFetch(path, options = {}) {
  const user = firebaseAuth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = new Error(`API error: ${response.status} ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  return response.json().then(decryptResponse);
}

export const questionApi = {
  /**
   * Fetch random questions filtered by formatSectionGroups and difficulty
   *
   * @param {Object} options
   * @param {string[]} [options.formatSectionGroups] - Array of section group IDs to filter by
   * @param {string} [options.difficulty] - 'easy' | 'medium' | 'hard' (ignored when stratified=true)
   * @param {string[]} [options.excludeIds] - Question IDs to exclude (for avoiding duplicates)
   * @param {number} [options.size] - Number of questions to return (default 10, max 50)
   * @param {boolean} [options.live] - Filter by live status (default true)
   * @param {boolean} [options.stratified] - If true, sample equally from each difficulty (default false)
   * @returns {Promise<{questions: Array, meta: {counts: Object, total: number, stratified: boolean}}>}
   *
   * @example
   * // Fetch 15 stratified questions (5 easy + 5 medium + 5 hard)
   * const { questions, meta } = await questionApi.getQuestions({
   *   formatSectionGroups: ['WS_ADD'],
   *   size: 15,
   *   stratified: true
   * });
   * console.log(meta.counts); // { easy: 5, medium: 5, hard: 5 }
   *
   * @example
   * // Fetch questions from multiple section groups for a session
   * const { questions } = await questionApi.getQuestions({
   *   formatSectionGroups: ['WS_ADD', 'WS_SUB', 'WS_MULT'],
   *   excludeIds: ['507f1f77bcf86cd799439011'],
   *   size: 30,
   *   stratified: true
   * });
   */
  async getQuestions({
    formatSectionGroups = [],
    difficulty,
    excludeIds = [],
    size = 10,
    live = true,
    stratified = false
  } = {}) {
    const params = new URLSearchParams();

    if (formatSectionGroups.length) {
      params.set('formatSectionGroups', formatSectionGroups.join(','));
    }

    if (difficulty && !stratified) {
      // difficulty is ignored when stratified=true
      params.set('difficulty', difficulty);
    }

    if (excludeIds.length) {
      params.set('excludeIds', excludeIds.join(','));
    }

    params.set('size', size.toString());

    if (!live) {
      params.set('live', 'false');
    }

    if (stratified) {
      params.set('stratified', 'true');
    }

    const queryString = params.toString();
    const path = queryString ? `/questions?${queryString}` : '/questions';

    return apiFetch(path);
  },

  /**
   * Prefetch questions for session with stratified difficulty distribution.
   * Use this before starting a practice/revision/diagnostic session.
   *
   * @param {string[]} formatSectionGroups - Array of section group IDs
   * @param {Object} [options]
   * @param {number} [options.perGroup=15] - Questions per section group (divided across difficulties)
   * @param {string[]} [options.excludeIds] - Question IDs to exclude
   * @returns {Promise<{questions: Array, meta: Object}>}
   */
  async prefetchForSession(formatSectionGroups, { perGroup = 15, excludeIds = [] } = {}) {
    const totalSize = Math.min(formatSectionGroups.length * perGroup, 50);

    return this.getQuestions({
      formatSectionGroups,
      excludeIds,
      size: totalSize,
      stratified: true
    });
  },

  /**
   * Fetch one random question per (sectionGroupId, difficulty) for the
   * diagnostic skills check. Single API call replaces ~70 individual fetches.
   *
   * @param {string[]} sectionGroupIds - All section group IDs in the diagnostic
   * @param {string[]} [excludeIds] - Question IDs to exclude
   * @returns {Promise<{questions: Object}>}
   *   questions: { [sgId]: { easy?: Object, medium?: Object, hard?: Object } }
   */
  async getDiagnosticQuestions(sectionGroupIds, excludeIds = []) {
    return apiFetch('/questions/diagnostic', {
      method: 'POST',
      body: JSON.stringify({ sectionGroupIds, excludeIds })
    });
  },

  /**
   * Convenience method: Fetch a single random question
   *
   * @param {string} formatSectionGroup - Single section group ID
   * @param {string} [difficulty] - Optional difficulty filter
   * @param {string[]} [excludeIds] - Question IDs to exclude
   * @returns {Promise<Object|null>} Single question or null if none found
   */
  async getRandomQuestion(formatSectionGroup, difficulty, excludeIds = []) {
    const { questions } = await this.getQuestions({
      formatSectionGroups: [formatSectionGroup],
      difficulty,
      excludeIds,
      size: 1
    });
    return questions[0] || null;
  }
};

export default questionApi;
